import React, { FC } from 'react'
import { CrewAppraisal, ReviewDepartment } from '../core/_requests'

type Props = {
  visible: boolean
  appraisal: CrewAppraisal | null
  onClose: () => void
}

const APPRAISAL_TYPE_LABEL = {
  MID_TERM: 'Mid Term',
  HOD_SIGNOFF: 'HOD Signoff',
  SPECIAL_CASE: 'Special Case',
  COMPLETION_OF_CONTRACT: 'Completion of Contract',
}

const AVERAGE_LABEL = {
  NTBE: 'NTBE (Not to be employed)',
  AVERAGE: 'Average',
  GOOD: 'Good',
  EXCELLENT: 'Excellent',
}

const BADGE_BY_AVERAGE: Record<string, string> = {
  NTBE: 'badge-light-danger',
  AVERAGE: 'badge-light-warning',
  GOOD: 'badge-light-success',
  EXCELLENT: 'badge-success',
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const FIELD_LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.55rem',
  color: '#3F4254',
  fontWeight: 600,
  fontSize: '0.95rem',
}

const SUMMARY_BOX_STYLE: React.CSSProperties = {
  minHeight: 46,
  padding: '0.65rem 0.9rem',
  border: '1px solid #E4E6EF',
  borderRadius: 6,
  background: '#F9F9F9',
  display: 'flex',
  alignItems: 'center',
}

const DEPT_CONFIG: { key: ReviewDepartment; label: string }[] = [
  { key: 'OPERATION', label: 'Operation' },
  { key: 'TECHNICAL', label: 'Technical' },
  { key: 'QHSC', label: 'QHSC' },
  { key: 'CREWING', label: 'Crewing' },
]

const getDeptReview = (appraisal: CrewAppraisal, dept: ReviewDepartment) => {
  switch (dept) {
    case 'OPERATION':
      return { remarks: appraisal.operationRemarks, rating: appraisal.operationRating, reviewedAt: appraisal.operationReviewedAt, reviewedBy: appraisal.operationReviewedByName }
    case 'TECHNICAL':
      return { remarks: appraisal.technicalRemarks, rating: appraisal.technicalRating, reviewedAt: appraisal.technicalReviewedAt, reviewedBy: appraisal.technicalReviewedByName }
    case 'QHSC':
      return { remarks: appraisal.qhscRemarks, rating: appraisal.qhscRating, reviewedAt: appraisal.qhscReviewedAt, reviewedBy: appraisal.qhscReviewedByName }
    case 'CREWING':
      return { remarks: appraisal.crewingRemarks, rating: appraisal.crewingRating, reviewedAt: appraisal.crewingReviewedAt, reviewedBy: appraisal.crewingReviewedByName }
  }
}

const ViewCrewAppraisalModal: FC<Props> = ({ visible, appraisal, onClose }) => {
  if (!visible || !appraisal) return null

  const resolvedAppraisalType = appraisal.appraisalType || 'MID_TERM'
  const resolvedAverageReport = appraisal.averageReport || 'AVERAGE'
  const resolvedRemarks = appraisal.remarks ?? ''

  const reviewedCount = appraisal.reviewCount ?? 0

  return (
    <div style={WRAP_STYLE}>
      <div className='bg-white rounded shadow-lg' style={{ width: '78vw', maxWidth: 1100, maxHeight: '88vh', overflow: 'auto' }}>
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <h5 className='m-0'>View Crew Appraisal</h5>
          <div className='d-flex align-items-center gap-3'>
            <span className='badge badge-light-info'>{reviewedCount}/4 Reviews</span>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>
        </div>

        <div className='p-5'>
          <div className='row g-4'>
            <div className='col-md-3'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Crew</label>
              <div style={SUMMARY_BOX_STYLE}>{appraisal.crewName || '-'}</div>
            </div>
            <div className='col-md-3'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Rank</label>
              <div style={SUMMARY_BOX_STYLE}>{appraisal.rankName || '-'}</div>
            </div>
            <div className='col-md-3'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Appraisal Type</label>
              <div style={SUMMARY_BOX_STYLE}>{APPRAISAL_TYPE_LABEL[resolvedAppraisalType] || resolvedAppraisalType || '-'}</div>
            </div>
            <div className='col-md-3'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Average Report</label>
              <div style={SUMMARY_BOX_STYLE}>
                <span className={`badge ${BADGE_BY_AVERAGE[resolvedAverageReport] || 'badge-light'}`}>
                  {AVERAGE_LABEL[resolvedAverageReport] || resolvedAverageReport || '-'}
                </span>
              </div>
            </div>

            <div className='col-12'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Remarks</label>
              <div className='border rounded p-3 bg-light' style={{ minHeight: 56, whiteSpace: 'pre-wrap' }}>
                {resolvedRemarks.trim() ? resolvedRemarks : 'No remarks'}
              </div>
            </div>

            {/* Department Reviews Section */}
            <div className='col-12 mt-4'>
              <h6 className='fw-bold text-dark mb-3'>Department Reviews</h6>
              <div className='row g-3'>
                {DEPT_CONFIG.map((dept) => {
                  const rev = getDeptReview(appraisal, dept.key)
                  const done = !!rev.reviewedAt
                  return (
                    <div className='col-md-6' key={dept.key}>
                      <div className='border rounded p-4'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <span className='fw-bold text-dark'>{dept.label}</span>
                          {done ? (
                            <span className='badge badge-light-success'>Reviewed</span>
                          ) : (
                            <span className='badge badge-light-secondary'>Pending</span>
                          )}
                        </div>
                        {done ? (
                          <>
                            <div className='d-flex align-items-center gap-2 mb-2'>
                              <span className='fw-bold text-dark fs-4'>{rev.rating}/10</span>
                            </div>
                            <div className='text-dark small' style={{ whiteSpace: 'pre-wrap' }}>
                              {rev.remarks || 'No remarks'}
                            </div>
                            <div className='text-muted mt-2' style={{ fontSize: 11 }}>
                              By {rev.reviewedBy || 'N/A'}
                              {rev.reviewedAt && ` on ${new Date(rev.reviewedAt).toLocaleString()}`}
                            </div>
                          </>
                        ) : (
                          <div className='text-muted small'>No review submitted yet.</div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        <div className='border-top d-flex justify-content-end px-5 py-4'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewCrewAppraisalModal
